import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import {
  Loader2, Send, Sparkles, Paperclip, MessageSquare, StickyNote, Mail,
  CalendarClock, DollarSign, FileText, Bell, X, Copy, Trash2, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useGmailConnection } from "@/hooks/useGmailConnection";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import {
  leadRef, getLeadCommunications, sendLeadEmail, generateLeadAIReply,
  addLeadInternalNote, deleteLeadInternalNote,
  sendLeadPaymentLink, sendLeadInvoice, sendLeadAppointmentConfirmation,
  sendLeadReminder, uploadLeadAttachment, leadAttachmentUrl,
  disconnectGmail, syncGmailInbox, sendViaGmail,
} from "@/lib/api";

const fmtDateTime = (iso) => { try { return format(new Date(iso), "PP p"); } catch { return "—"; } };

// =============================================================================
// Main panel
// =============================================================================
export default function LeadCommunicationsPanel({ lead, onLeadUpdate }) {
  const [tab, setTab] = useState("thread");
  const [thread, setThread] = useState({ emails: [], notes: [] });
  const [loading, setLoading] = useState(true);
  const { gmail, connecting: gmailConnecting, connect: connectGmail, refresh: refreshGmail } =
    useGmailConnection(lead?.id);
  const [gmailBusy, setGmailBusy] = useState(false);

  const [showCompose, setShowCompose] = useState(false);
  const [composeSeed, setComposeSeed] = useState(null); // {subject, body, purpose}
  const [showPayment, setShowPayment] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showAppointment, setShowAppointment] = useState(false);
  const [showReminder, setShowReminder] = useState(false);

  const ref = leadRef(lead?.id);

  const refresh = async () => {
    if (!lead?.id) return;
    try {
      setLoading(true);
      const data = await getLeadCommunications(lead.id);
      setThread({ emails: data.emails || [], notes: data.notes || [] });
    } catch {
      toast.error("Failed to load communications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [lead?.id]);

  const doDisconnect = async () => {
    if (!window.confirm("Disconnect this Gmail account? Sending will fall back to Resend.")) return;
    try {
      await disconnectGmail();
      toast.success("Gmail disconnected.");
      refreshGmail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disconnect failed."); }
  };

  const doSync = async () => {
    try {
      setGmailBusy(true);
      const res = await syncGmailInbox();
      toast.success(`Synced · ${res.routed_to_leads} to leads · ${res.routed_to_facilities} to facilities`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gmail sync failed.");
    } finally { setGmailBusy(false); }
  };

  const openCompose = (seed = null) => {
    setComposeSeed(seed);
    setShowCompose(true);
  };

  const feed = useMemo(() => {
    const items = [
      ...thread.emails.map((e) => ({ ...e, __k: "email" })),
      ...thread.notes.map((n) => ({ ...n, __k: "note" })),
    ];
    items.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    return items;
  }, [thread]);

  const runAIReply = async () => {
    try {
      const res = await generateLeadAIReply(lead.id, {});
      openCompose({ subject: res.subject, body: res.body, purpose: "custom" });
      toast.success("AI drafted a reply — review and edit.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI reply failed.");
    }
  };

  return (
    <div className="space-y-4" data-testid="lead-communications-panel">
      {/* Gmail connect strip */}
      <div
        className="rounded-lg border p-3 flex items-center justify-between gap-3 flex-wrap"
        style={{
          borderColor: gmail.connected ? "rgba(16,185,129,0.35)" : "#E2E8F0",
          background: gmail.connected ? "rgba(16,185,129,0.06)" : "#F8FAFC",
        }}
        data-testid="gmail-strip"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-lg grid place-items-center shrink-0"
            style={{ background: gmail.connected ? "rgba(16,185,129,0.15)" : "rgba(148,163,184,0.15)",
                     color: gmail.connected ? "#065F46" : "#64748B" }}>
            <Mail className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold" style={{ color: "#0B2545" }}>
              {gmail.connected ? "Gmail connected" : "Gmail not connected"}
            </div>
            <div className="text-[11px] truncate" style={{ color: "#64748B" }}>
              {gmail.connected
                ? `Signed in as ${gmail.email || ""} · Sending goes through your Gmail`
                : "Connect your company Gmail to send from your own address and auto-sync replies."}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {gmail.connected ? (
            <>
              <Button size="sm" variant="outline" onClick={doSync} disabled={gmailBusy}
                className="h-8 text-xs" data-testid="gmail-sync">
                {gmailBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" style={{ color: "#D4AF37" }} />}
                Sync inbox
              </Button>
              <Button size="sm" variant="outline" onClick={doDisconnect}
                className="h-8 text-xs" data-testid="gmail-disconnect">
                Disconnect
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={connectGmail} disabled={gmailConnecting}
              className="h-8 text-xs" style={{ background: "#0B2545", color: "#FFF" }}
              data-testid="gmail-connect">
              {gmailConnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
              {gmailConnecting ? "Connecting…" : "Connect Gmail"}
            </Button>
          )}
        </div>
      </div>

      {/* Header w/ ref + quick actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4" style={{ color: "#2563EB" }} />
          <div className="text-sm font-semibold" style={{ color: "#0B2545" }}>Communications</div>
          <span
            className="inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-bold tracking-wider"
            style={{ borderColor: "#E2E8F0", background: "#F8FAFC", color: "#0B2545" }}
            title="Reference number attached to every email footer"
            data-testid="lead-ref-chip"
          >
            {ref}
            <button
              type="button" onClick={() => { navigator.clipboard?.writeText(ref); toast.success("Copied"); }}
              className="ml-1.5 opacity-60 hover:opacity-100"
              data-testid="lead-ref-copy"
              aria-label="Copy reference"
            >
              <Copy className="h-3 w-3" />
            </button>
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <QuickBtn onClick={() => openCompose(null)} icon={Mail}         label="Compose"                testId="qa-compose" />
          <QuickBtn onClick={runAIReply}              icon={Sparkles}     label="AI Reply"    accent="#D4AF37" testId="qa-ai-reply" />
          <QuickBtn onClick={() => setShowPayment(true)}     icon={DollarSign}   label="Payment Link"           testId="qa-payment" />
          <QuickBtn onClick={() => setShowInvoice(true)}     icon={FileText}     label="Invoice"                testId="qa-invoice" />
          <QuickBtn onClick={() => setShowAppointment(true)} icon={CalendarClock} label="Appt Confirm"           testId="qa-appointment" />
          <QuickBtn onClick={() => setShowReminder(true)}    icon={Bell}         label="Reminder"               testId="qa-reminder" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 h-9" style={{ background: "#F1F5F9" }}>
          <TabsTrigger value="thread" data-testid="tab-thread">
            <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> Thread ({feed.length})
          </TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">
            <StickyNote className="h-3.5 w-3.5 mr-1.5" /> Internal Notes ({thread.notes.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="thread" className="mt-3 space-y-2">
          {loading ? (
            <div className="py-6 flex items-center gap-2 text-xs" style={{ color: "#94A3B8" }}>
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : feed.length === 0 ? (
            <div className="rounded-lg border border-dashed py-8 text-center text-xs"
              style={{ borderColor: "#E2E8F0", color: "#94A3B8" }}
              data-testid="thread-empty"
            >
              No messages yet. Send the first email using the quick actions above.
            </div>
          ) : (
            feed.map((item) => (
              item.__k === "email"
                ? <EmailItem key={item.id} m={item} />
                : <NoteItem key={item.id} n={item} onDelete={async () => {
                    await deleteLeadInternalNote(lead.id, item.id);
                    toast.success("Note deleted");
                    refresh();
                  }} />
            ))
          )}
        </TabsContent>

        <TabsContent value="notes" className="mt-3 space-y-3">
          <NoteComposer lead={lead} onAdded={refresh} />
          {thread.notes.length === 0 ? (
            <div className="rounded-lg border border-dashed py-6 text-center text-xs"
              style={{ borderColor: "#E2E8F0", color: "#94A3B8" }}
              data-testid="notes-empty"
            >
              No internal notes yet.
            </div>
          ) : (
            thread.notes.map((n) => (
              <NoteItem key={n.id} n={n} onDelete={async () => {
                await deleteLeadInternalNote(lead.id, n.id);
                toast.success("Note deleted");
                refresh();
              }} />
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ComposeDialog
        lead={lead} open={showCompose} seed={composeSeed} gmail={gmail}
        onClose={() => { setShowCompose(false); setComposeSeed(null); }}
        onSent={() => { refresh(); onLeadUpdate?.(); }}
      />
      <PaymentLinkDialog lead={lead} open={showPayment}
        onClose={() => setShowPayment(false)}
        onSent={() => { refresh(); onLeadUpdate?.(); }}
      />
      <InvoiceDialog lead={lead} open={showInvoice}
        onClose={() => setShowInvoice(false)}
        onSent={() => { refresh(); onLeadUpdate?.(); }}
      />
      <AppointmentDialog lead={lead} open={showAppointment}
        onClose={() => setShowAppointment(false)}
        onSent={() => { refresh(); onLeadUpdate?.(); }}
      />
      <ReminderDialog lead={lead} open={showReminder}
        onClose={() => setShowReminder(false)}
        onSent={() => { refresh(); }}
      />
    </div>
  );
}

// =============================================================================
// Atoms
// =============================================================================
const QuickBtn = ({ onClick, icon: Icon, label, accent, testId }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-xs font-semibold transition hover:shadow-sm"
    style={{ borderColor: "#E2E8F0", background: "#FFF", color: "#0B2545" }}
    data-testid={testId}
  >
    <Icon className="h-3.5 w-3.5" style={{ color: accent || "#2563EB" }} />
    {label}
  </button>
);

const EmailItem = ({ m }) => {
  const isOut = m.direction === "outbound";
  const statusPill = {
    sent:      { label: "Sent",      color: "#1D4ED8", bg: "rgba(59,130,246,0.12)" },
    delivered: { label: "Delivered", color: "#065F46", bg: "rgba(16,185,129,0.14)" },
    opened:    { label: "Opened",    color: "#7A5E12", bg: "rgba(212,175,55,0.16)" },
    replied:   { label: "Replied",   color: "#6B21A8", bg: "rgba(168,85,247,0.14)" },
    bounced:   { label: "Bounced",   color: "#991B1B", bg: "rgba(220,38,38,0.10)" },
    failed:    { label: "Failed",    color: "#991B1B", bg: "rgba(220,38,38,0.10)" },
    queued:    { label: "Queued",    color: "#64748B", bg: "rgba(148,163,184,0.14)" },
  }[m.status] || { label: m.status || "Sent", color: "#64748B", bg: "rgba(148,163,184,0.14)" };

  return (
    <div
      className="rounded-lg border p-3 space-y-2"
      style={{
        borderColor: isOut ? "#E2E8F0" : "rgba(37,99,235,0.4)",
        background: isOut ? "#FFF" : "rgba(37,99,235,0.04)",
      }}
      data-testid={`comm-email-${m.id}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-6 w-6 rounded-full grid place-items-center shrink-0"
            style={{ background: isOut ? "rgba(11,37,69,0.08)" : "rgba(37,99,235,0.12)", color: isOut ? "#0B2545" : "#2563EB" }}>
            {isOut ? <Mail className="h-3 w-3" /> : <Send className="h-3 w-3 -rotate-45" />}
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold truncate" style={{ color: "#0B2545" }}>{m.subject}</div>
            <div className="text-[11px] truncate" style={{ color: "#64748B" }}>
              {isOut ? "→ " + (m.to || "customer") : "← " + (m.from || "customer")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wider"
            style={{ background: statusPill.bg, color: statusPill.color }}>
            {statusPill.label}
          </span>
          <span className="text-[11px]" style={{ color: "#94A3B8" }}>{fmtDateTime(m.created_at)}</span>
        </div>
      </div>
      {m.attachments?.length ? (
        <div className="flex flex-wrap gap-1.5">
          {m.attachments.map((a, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border"
              style={{ borderColor: "#E2E8F0", background: "#F8FAFC", color: "#475569" }}>
              <Paperclip className="h-2.5 w-2.5" /> {a.filename}
            </span>
          ))}
        </div>
      ) : null}
      {m.body_html ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-[11px]" style={{ color: "#2563EB" }}>Show message</summary>
          <div
            className="mt-2 max-h-64 overflow-auto rounded border px-3 py-2 text-xs prose-sm"
            style={{ borderColor: "#F1F5F9", background: "#FAFAFA" }}
            dangerouslySetInnerHTML={{ __html: m.body_html }}
          />
        </details>
      ) : null}
    </div>
  );
};

const NoteItem = ({ n, onDelete }) => (
  <div
    className="rounded-lg border p-3"
    style={{ borderColor: "rgba(212,175,55,0.35)", background: "rgba(212,175,55,0.06)" }}
    data-testid={`comm-note-${n.id}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1">
        <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#7A5E12" }}>
          Internal note · {n.author}
        </div>
        <div className="mt-1 text-sm whitespace-pre-wrap" style={{ color: "#0F172A" }}>{n.text}</div>
        <div className="mt-1 text-[11px]" style={{ color: "#94A3B8" }}>{fmtDateTime(n.created_at)}</div>
      </div>
      <button
        onClick={() => { if (window.confirm("Delete this internal note?")) onDelete(); }}
        className="p-1 rounded hover:bg-black/5" title="Delete note"
        data-testid={`comm-note-delete-${n.id}`}
      >
        <Trash2 className="h-3.5 w-3.5" style={{ color: "#991B1B" }} />
      </button>
    </div>
  </div>
);

const NoteComposer = ({ lead, onAdded }) => {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await addLeadInternalNote(lead.id, text.trim());
      setText(""); toast.success("Note saved"); onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save note.");
    } finally { setSaving(false); }
  };
  return (
    <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: "#E2E8F0", background: "#FFF" }}>
      <Label className="text-xs">Add internal note (staff-only)</Label>
      <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
        placeholder="Not visible to the customer…"
        data-testid="note-input"
      />
      <div className="flex justify-end">
        <Button size="sm" className="h-8 text-xs"
          onClick={submit} disabled={saving || !text.trim()}
          style={{ background: "#7A5E12", color: "#FFF" }}
          data-testid="note-submit"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <StickyNote className="h-3.5 w-3.5 mr-1" />}
          Save note
        </Button>
      </div>
    </div>
  );
};

// =============================================================================
// Compose Dialog (with AI + attachments)
// =============================================================================
const ComposeDialog = ({ lead, open, seed, gmail, onClose, onSent }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]); // [{file_id, filename, size_bytes}]
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSubject(seed?.subject || "");
      setBody(seed?.body || "");
      setAttachments([]); setError("");
    }
  }, [open, seed]);

  const runAI = async () => {
    try {
      setError("");
      const res = await generateLeadAIReply(lead.id, {});
      setSubject(res.subject); setBody(res.body);
      toast.success("AI drafted the reply.");
    } catch (e) {
      setError(e?.response?.data?.detail || "AI draft failed.");
    }
  };

  const uploadFile = async (f) => {
    try {
      setUploading(true);
      const res = await uploadLeadAttachment(lead.id, f);
      setAttachments((prev) => [...prev, { file_id: res.file_id, filename: res.filename, size_bytes: res.size_bytes }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      setError("Subject and body are required.");
      return;
    }
    setSending(true); setError("");
    try {
      if (gmail?.connected) {
        await sendViaGmail({
          kind: "lead",
          entity_id: lead.id,
          subject: subject.trim(),
          body_text: body.trim(),
          attachment_ids: attachments.map((a) => a.file_id),
          purpose: seed?.purpose || "custom",
        });
        toast.success(`Sent from ${gmail.email} · Gmail`);
      } else {
        await sendLeadEmail(lead.id, {
          subject: subject.trim(),
          body_text: body.trim(),
          attachment_ids: attachments.map((a) => a.file_id),
          purpose: seed?.purpose || "custom",
        });
        toast.success("Email sent — delivery tracked in thread.");
      }
      onSent?.(); onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Send failed.";
      setError(detail); toast.error(detail);
    } finally { setSending(false); }
  };

  if (!open) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="lead-compose-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b" style={{ borderColor: "#F1F5F9" }}>
          <DialogTitle>Compose email to {lead?.full_name || lead?.email}</DialogTitle>
          <DialogDescription>
            {gmail?.connected
              ? <>Sent from <b>{gmail.email}</b> via Gmail · Reference <b>{leadRef(lead?.id)}</b> attached automatically.</>
              : <>Sent via Resend with reference <b>{leadRef(lead?.id)}</b> in the footer.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {error ? (
            <div className="rounded-lg border p-3 text-xs"
              style={{ borderColor: "rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}
              data-testid="compose-error"
            >{error}</div>
          ) : null}
          <div className="flex justify-end">
            <Button size="sm" onClick={runAI} className="h-8 text-xs"
              style={{ background: "#D4AF37", color: "#0F172A" }}
              data-testid="compose-ai-draft"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1" /> Draft with AI
            </Button>
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input value={lead?.email || ""} disabled className="mt-1" data-testid="compose-to" />
          </div>
          <div>
            <Label className="text-xs">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1"
              data-testid="compose-subject"
            />
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea rows={11} value={body} onChange={(e) => setBody(e.target.value)}
              className="mt-1 text-sm" placeholder="Type your message…"
              data-testid="compose-body"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Attachments (optional)</Label>
            <input
              ref={fileInputRef} type="file" className="hidden"
              onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
              data-testid="compose-file-input"
            />
            <Button variant="outline" size="sm" className="h-8 text-xs"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="compose-add-attachment"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Paperclip className="h-3.5 w-3.5 mr-1" />}
              Add file
            </Button>
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {attachments.map((a) => (
                  <span key={a.file_id}
                    className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border"
                    style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}
                    data-testid={`compose-attachment-${a.file_id}`}
                  >
                    <Paperclip className="h-3 w-3" /> {a.filename}
                    <button onClick={() => setAttachments((prev) => prev.filter((x) => x.file_id !== a.file_id))}
                      className="ml-1 opacity-60 hover:opacity-100">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t" style={{ borderColor: "#F1F5F9" }}>
          <Button variant="outline" onClick={onClose} className="h-9">Cancel</Button>
          <Button onClick={send} disabled={sending || !subject || !body}
            className="h-9" style={{ background: "#0B2545", color: "#FFF" }}
            data-testid="compose-send"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
            Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// Payment / Invoice / Appointment / Reminder Dialogs
// =============================================================================
const PaymentLinkDialog = ({ lead, open, onClose, onSent }) => {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("Service payment");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setAmount(""); setDescription("Service payment"); setError(""); } }, [open]);
  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { setError("Amount must be > 0"); return; }
    setSaving(true); setError("");
    try {
      await sendLeadPaymentLink(lead.id, { amount: amt, description });
      toast.success("Payment link emailed"); onSent?.(); onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail || "Failed to send"; setError(d); toast.error(d);
    } finally { setSaving(false); }
  };
  if (!open) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="payment-link-dialog">
        <DialogHeader>
          <DialogTitle>Send payment link</DialogTitle>
          <DialogDescription>Creates a Stripe checkout link and emails it to {lead?.email}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error && (<div className="rounded-lg border p-3 text-xs"
              style={{ borderColor: "rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}
              data-testid="payment-error"
            >{error}</div>)}
          <div>
            <Label className="text-xs">Amount (USD)</Label>
            <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" data-testid="payment-amount" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" data-testid="payment-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="h-9">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="h-9" style={{ background: "#0B2545", color: "#FFF" }} data-testid="payment-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <DollarSign className="h-4 w-4 mr-1.5" />} Send Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const InvoiceDialog = ({ lead, open, onClose, onSent }) => {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("Invoice");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setAmount(lead?.invoice_amount || "");
      setDescription(lead?.invoice_description || "Invoice");
      setError("");
    }
  }, [open, lead?.id]);
  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { setError("Amount must be > 0"); return; }
    setSaving(true); setError("");
    try {
      await sendLeadInvoice(lead.id, { amount: amt, description });
      toast.success("Invoice emailed"); onSent?.(); onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail || "Failed to send"; setError(d); toast.error(d);
    } finally { setSaving(false); }
  };
  if (!open) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="invoice-dialog">
        <DialogHeader>
          <DialogTitle>Send invoice</DialogTitle>
          <DialogDescription>Emails a Stripe-backed invoice link to {lead?.email}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error && (<div className="rounded-lg border p-3 text-xs"
              style={{ borderColor: "rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}
              data-testid="invoice-error"
            >{error}</div>)}
          <div>
            <Label className="text-xs">Amount (USD)</Label>
            <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" data-testid="invoice-amount" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" data-testid="invoice-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="h-9">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="h-9" style={{ background: "#0B2545", color: "#FFF" }} data-testid="invoice-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileText className="h-4 w-4 mr-1.5" />} Send Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AppointmentDialog = ({ lead, open, onClose, onSent }) => {
  const [service, setService] = useState("");
  const [date, setDate] = useState("");
  const [timeWindow, setTimeWindow] = useState("");
  const [location, setLocation] = useState("");
  const [prep, setPrep] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setService((lead?.services || [])[0] || "");
      setDate(""); setTimeWindow(""); setLocation(lead?.service_address || ""); setPrep(""); setError("");
    }
  }, [open, lead?.id]);
  const submit = async () => {
    if (!service || !date || !timeWindow) { setError("Service, date and arrival window are required."); return; }
    setSaving(true); setError("");
    try {
      await sendLeadAppointmentConfirmation(lead.id, {
        service, date, time_window: timeWindow, location, prep_instructions: prep,
      });
      toast.success("Appointment confirmation emailed"); onSent?.(); onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail || "Failed to send"; setError(d); toast.error(d);
    } finally { setSaving(false); }
  };
  if (!open) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="appointment-dialog">
        <DialogHeader>
          <DialogTitle>Send appointment confirmation</DialogTitle>
          <DialogDescription>Emails the customer a branded appointment confirmation.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error && (<div className="rounded-lg border p-3 text-xs"
              style={{ borderColor: "rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}
              data-testid="appointment-error"
            >{error}</div>)}
          <div>
            <Label className="text-xs">Service</Label>
            <Input value={service} onChange={(e) => setService(e.target.value)} className="mt-1" placeholder="e.g. Mobile Phlebotomy — Fasting labs" data-testid="appt-service" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="appt-date" />
            </div>
            <div>
              <Label className="text-xs">Arrival window</Label>
              <Input value={timeWindow} onChange={(e) => setTimeWindow(e.target.value)} className="mt-1" placeholder="8:00 – 10:00 AM" data-testid="appt-window" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Location</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} className="mt-1" placeholder="Customer address" data-testid="appt-location" />
          </div>
          <div>
            <Label className="text-xs">Prep instructions (optional)</Label>
            <Textarea rows={3} value={prep} onChange={(e) => setPrep(e.target.value)} className="mt-1" placeholder="Fasting instructions, IDs to bring, etc." data-testid="appt-prep" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="h-9">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="h-9" style={{ background: "#0B2545", color: "#FFF" }} data-testid="appt-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CalendarClock className="h-4 w-4 mr-1.5" />} Send Confirmation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReminderDialog = ({ lead, open, onClose, onSent }) => {
  const [reason, setReason] = useState("your upcoming appointment");
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) { setReason("your upcoming appointment"); setDate(lead?.appointment_date || ""); setError(""); }
  }, [open, lead?.id]);
  const submit = async () => {
    setSaving(true); setError("");
    try {
      await sendLeadReminder(lead.id, { reason, date: date || undefined });
      toast.success("Reminder emailed"); onSent?.(); onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail || "Failed to send"; setError(d); toast.error(d);
    } finally { setSaving(false); }
  };
  if (!open) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="reminder-dialog">
        <DialogHeader>
          <DialogTitle>Send reminder</DialogTitle>
          <DialogDescription>Emails a friendly reminder to {lead?.email}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error && (<div className="rounded-lg border p-3 text-xs"
              style={{ borderColor: "rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}
              data-testid="reminder-error"
            >{error}</div>)}
          <div>
            <Label className="text-xs">What is the reminder about?</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" data-testid="reminder-reason" />
          </div>
          <div>
            <Label className="text-xs">Date (optional)</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="reminder-date" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="h-9">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="h-9" style={{ background: "#0B2545", color: "#FFF" }} data-testid="reminder-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Bell className="h-4 w-4 mr-1.5" />} Send Reminder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
